import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { usePageAccess } from '@/hooks/usePageAccess'
import {
  getFixedAssetMonthsSummary,
  getFixedAssetSyncCheck,
  type MonthSummaryItem,
} from '@/services/fixedAssets'
import { useFixedAssetsSyncStore } from '@/store/fixedAssetsSyncStore'
import { useFixedAssetsNavStore } from '@/store/fixedAssetsNavStore'
import FixedAssetModal from './FixedAssetModal'
import FixedAssetsSyncBanner from './FixedAssetsSyncBanner'

const SIX_HOURS_MS = 6 * 60 * 60 * 1000

const tabs = [
  { to: '/fixed-assets/allocate', label: 'Allocate' },
  { to: '/fixed-assets/schedules', label: 'Schedules' },
] as const

export default function FixedAssetsLayout() {
  usePageAccess('fixed-assets')
  const [monthsSummary, setMonthsSummary] = useState<MonthSummaryItem[]>([])
  const assetModalOpen = useFixedAssetsSyncStore((state) => state.assetModalOpen)
  const assetModalAsset = useFixedAssetsSyncStore((state) => state.assetModalAsset)
  const assetModalPrefill = useFixedAssetsSyncStore((state) => state.assetModalPrefill)
  const closeAssetModal = useFixedAssetsSyncStore((state) => state.closeAssetModal)
  const fetchLatest = useFixedAssetsSyncStore((state) => state.fetchLatest)
  const notifyRegisterChanged = useFixedAssetsSyncStore((state) => state.notifyRegisterChanged)
  const fetchNavStatus = useFixedAssetsNavStore((state) => state.fetchNavStatus)

  useEffect(() => {
    void fetchNavStatus({ refresh: true })
  }, [fetchNavStatus])

  useEffect(() => {
    getFixedAssetMonthsSummary()
      .then((response) => setMonthsSummary(response.months))
      .catch((err) => console.error(err))
  }, [])

  useEffect(() => {
    let cancelled = false
    const { latest: cached, setLatest: storeSetLatest } = useFixedAssetsSyncStore.getState()
    const needsRefresh =
      cached == null ||
      Date.now() - new Date(cached.ran_at).getTime() > SIX_HOURS_MS

    if (!needsRefresh) return

    void (async () => {
      try {
        const check = await getFixedAssetSyncCheck({ refresh: true })
        if (!cancelled) storeSetLatest(check)
      } catch (err) {
        console.error('Failed to refresh fixed-assets sync check:', err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="bg-white border-b border-gray-200 shrink-0">
        <div className="px-4 lg:px-6 py-3">
          <h1 className="text-xl font-semibold text-gray-900">Fixed Assets</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Depreciation and amortization schedules, with a monthly journal entry to QuickBooks
          </p>
        </div>

        <div className="border-t border-gray-200 px-4 lg:px-6 py-4">
          <div className="overflow-x-auto">
            <div
              className="inline-flex gap-2 bg-gray-200 p-1 rounded-lg min-w-max"
              role="tablist"
              aria-label="Fixed Assets sections"
            >
              {tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  role="tab"
                  className={({ isActive }) =>
                    `px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-white text-brand-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
        <div className="shrink-0">
          <FixedAssetsSyncBanner />
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </div>
      </div>

      <FixedAssetModal
        open={assetModalOpen}
        onClose={closeAssetModal}
        onSaved={(createdId) => {
          void fetchLatest(true)
          notifyRegisterChanged(createdId)
        }}
        asset={assetModalAsset}
        prefill={assetModalPrefill}
        monthsSummary={monthsSummary}
      />
    </div>
  )
}
